import {
    CellBadge,
    CellDatetime,
    DataTable,
    DataTableColumnHeader,
    DataTableControls,
    DataTablePagination,
} from 'src/registry/components/table';
import { useIntl } from 'react-intl';
import useSearch from 'src/registry/hooks/useSearch';
import { createColumnHelper } from '@tanstack/react-table';
import { booleanBadgeOptions } from 'src/utils/constants';
import { servicePlansMessages } from 'src/features/servicePlans/utils/messages';
import useFilters from 'src/registry/hooks/useFilters';
import { Subscription } from 'src/features/subscriptions/utils/types';
import { useListSubscriptions } from 'src/features/subscriptions/api/useListSubscriptions';
import { subscriptionsMessages } from 'src/features/subscriptions/utils/messages';
import { customersMessages } from 'src/features/customers/utils/messages';
import { SUBSCRIPTIONS_STATUS_BADGE_OPTIONS } from 'src/features/subscriptions/utils/constants';
import CreateSubscriptionFormModal from 'src/features/subscriptions/components/forms/CreateSubscriptionFormModal';
import { CellLinkWithIcon } from 'src/registry/components/table/cells/CellLinkWithIcon';
import CellSubscriptionActions from 'src/features/subscriptions/components/tables/CellSubscriptionActions';
import usePagination from 'src/registry/hooks/usePagination';
import { INPUT_TYPE } from 'src/registry/components/table/utils/constants';
import useSorting from 'src/registry/hooks/useSorting';
import { useParams } from 'react-router-dom';
import useHasPermissions from 'src/utils/useHasPermissions';
import { PERMISSION } from 'src/features/auth/types';

const SubscriptionsTable = () => {
    const { formatMessage } = useIntl();
    const { customerId = '' } = useParams();

    const permissionManageSubscription = useHasPermissions([PERMISSION.MANAGE_SUBSCRIPTIONS]);

    const { querySearchOptions, ...search } = useSearch({ globalFilterKey: 'name' });
    const { queryFiltersOptions, ...filters } = useFilters();
    const { queryPaginationOptions, ...pagination } = usePagination();
    const { querySortingOptions, ...sorting } = useSorting({
        initialSortingState: [{ id: 'start_date', desc: false }],
    });

    const {
        data: subscriptionsData,
        isLoading,
        isError,
    } = useListSubscriptions({
        ...querySearchOptions,
        ...queryFiltersOptions,
        ...queryPaginationOptions,
        ...querySortingOptions,
        ...(customerId && { customer: customerId }),
    });

    const permissionAccessCustomers = useHasPermissions([PERMISSION.ACCESS_CUSTOMERS]);
    const permissionAccessServicePlans = useHasPermissions([PERMISSION.ACCESS_SERVICE_PLANS]);

    const columnHelper = createColumnHelper<Subscription>();

    const columns = [
        columnHelper.accessor('reference', {
            enableSorting: true,
            header: DataTableColumnHeader,
            cell: (ctx) => <CellLinkWithIcon {...ctx} to={`/subscriptions/${ctx.row.original.id}`} />,
            meta: {
                title: formatMessage(subscriptionsMessages.refNumber),
            },
        }),
        columnHelper.accessor('service_plan.name', {
            enableSorting: true,
            header: DataTableColumnHeader,
            enableHiding: true,
            cell: (ctx) => {
                const servicePlanId = ctx.row.original.service_plan.id;
                return (
                    <CellLinkWithIcon
                        {...ctx}
                        to={`/service-plans/${servicePlanId}`}
                        hasPermissions={permissionAccessServicePlans}
                    />
                );
            },
            meta: {
                title: formatMessage(servicePlansMessages.servicePlan),
            },
        }),
        columnHelper.accessor('customer.name', {
            enableSorting: true,
            header: DataTableColumnHeader,
            enableHiding: true,
            cell: (ctx) => {
                const customerId = ctx.row.original.customer.id;
                return (
                    <CellLinkWithIcon
                        {...ctx}
                        to={`/customers/${customerId}`}
                        hasPermissions={permissionAccessCustomers}
                    />
                );
            },
            meta: {
                title: formatMessage(customersMessages.customer),
            },
        }),
        columnHelper.accessor('external_id', {
            enableSorting: true,
            header: DataTableColumnHeader,
            enableHiding: true,
            enableColumnFilter: true,
            meta: {
                title: formatMessage(subscriptionsMessages.externalId),
                inputType: INPUT_TYPE.TEXT,
            },
        }),
        columnHelper.accessor('start_date', {
            header: DataTableColumnHeader,
            enableHiding: true,
            enableSorting: true,
            cell: CellDatetime,
            meta: {
                title: formatMessage(subscriptionsMessages.startDate),
            },
        }),
        columnHelper.accessor('end_date', {
            header: DataTableColumnHeader,
            enableHiding: true,
            enableSorting: true,
            cell: CellDatetime,
            meta: {
                title: formatMessage(subscriptionsMessages.endDate),
            },
        }),
        columnHelper.accessor('status', {
            enableSorting: true,
            header: DataTableColumnHeader,
            enableHiding: true,
            enableColumnFilter: true,
            cell: (ctx) => {
                return ctx.row.original.status !== null ? <CellBadge {...ctx} /> : '-';
            },
            meta: {
                title: formatMessage(subscriptionsMessages.status),
                badgeOptions: SUBSCRIPTIONS_STATUS_BADGE_OPTIONS,
                inputType: INPUT_TYPE.MULTI_SELECT,
            },
        }),
        columnHelper.accessor('cancelled_at', {
            header: DataTableColumnHeader,
            enableHiding: true,
            enableSorting: true,
            cell: CellDatetime,
            meta: {
                title: formatMessage(subscriptionsMessages.cancelledAt),
            },
        }),
        columnHelper.accessor('locked_at', {
            header: DataTableColumnHeader,
            enableHiding: true,
            enableSorting: true,
            cell: CellDatetime,
            meta: {
                title: formatMessage(subscriptionsMessages.lockedAt),
            },
        }),
        columnHelper.accessor('stopped_on', {
            header: DataTableColumnHeader,
            enableHiding: true,
            enableSorting: true,
            cell: CellDatetime,
            meta: {
                title: formatMessage(subscriptionsMessages.stoppedOn),
            },
        }),
        columnHelper.accessor('has_active_billing', {
            enableSorting: true,
            header: DataTableColumnHeader,
            enableHiding: true,
            cell: CellBadge,
            meta: {
                title: formatMessage(subscriptionsMessages.activeBilling),
                badgeOptions: booleanBadgeOptions,
            },
        }),
        columnHelper.display({
            id: 'actions',
            header: DataTableColumnHeader,
            cell: CellSubscriptionActions,
            maxSize: 45,
        }),
    ];

    return (
        <DataTable<Subscription, unknown>
            columns={columns}
            data={subscriptionsData}
            semantics={{
                title: formatMessage(subscriptionsMessages.subscriptions),
                entryName: formatMessage(subscriptionsMessages.subscription).toLowerCase(),
                entryNames: formatMessage(subscriptionsMessages.subscriptionsTableBadge).toLowerCase(),
                placeholder: formatMessage(subscriptionsMessages.subscription).toLowerCase(),
            }}
            search={search}
            filters={filters}
            sorting={sorting}
            pagination={pagination}
            isError={isError}
            isLoading={isLoading}
            getRowId={(row) => row.id.toString()}
            components={{
                controls: DataTableControls,
                pagination: DataTablePagination,
                ...(permissionManageSubscription && { newEntry: CreateSubscriptionFormModal }),
            }}
        />
    );
};

export default SubscriptionsTable;
